(() => {
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.floor(seconds % 60);
    return `${minutes}:${String(remainder).padStart(2, '0')}`;
  };

  const initialise = (root = document) => {
    const players = [...(root.querySelectorAll?.('[data-pu-product-video-ui]') || [])];
    players.forEach((container) => {
      if (container.dataset.productVideoReady === 'true') return;

      const media = container.querySelector('[data-pu-product-video-media]');
      const toggle = container.querySelector('[data-pu-product-video-toggle]');
      const controls = container.querySelector('[data-pu-product-video-controls]');
      const progress = container.querySelector('[data-pu-product-video-progress]');
      const time = container.querySelector('[data-pu-product-video-time]');
      const mute = container.querySelector('[data-pu-product-video-mute]');
      const fullscreen = container.querySelector('[data-pu-product-video-fullscreen]');
      if (!media || !toggle || !controls || !progress || !time || !mute || !fullscreen) return;

      container.dataset.productVideoReady = 'true';
      container.classList.add('is-enhanced');

      // Poster and metadata are fetched when the player approaches the viewport,
      // never during the initial load; playback still starts on the first tap.
      let approachReady = false;
      const prepareMedia = () => {
        if (approachReady) return;
        approachReady = true;
        if (media.dataset.poster && !media.poster) media.poster = media.dataset.poster;
        if (media.preload === 'none') media.preload = 'metadata';
      };
      if (typeof window.IntersectionObserver === 'function') {
        const approach = new window.IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          approach.disconnect();
          prepareMedia();
        }, { rootMargin: '600px 0px' });
        approach.observe(container);
      } else prepareMedia();
      media.addEventListener('play', prepareMedia);
      container.dataset.videoState = 'paused';
      container.dataset.muted = String(media.muted);
      container.dataset.fullscreen = 'false';
      media.controls = false;
      toggle.hidden = false;
      controls.hidden = false;

      const syncPlayback = () => {
        const isPlaying = !media.paused && !media.ended;
        container.dataset.videoState = isPlaying ? 'playing' : 'paused';
        toggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      };

      const syncTime = () => {
        const duration = Number.isFinite(media.duration) ? media.duration : 0;
        const current = Number.isFinite(media.currentTime) ? media.currentTime : 0;
        progress.max = String(duration || 100);
        progress.value = String(current);
        progress.style.setProperty('--pu-video-progress', `${duration ? Math.min(100, (current / duration) * 100) : 0}%`);
        progress.setAttribute('aria-valuetext', `${formatTime(current)} of ${formatTime(duration)}`);
        time.value = `${formatTime(current)} / ${formatTime(duration)}`;
        time.textContent = time.value;
      };

      const togglePlayback = () => {
        if (media.paused || media.ended) media.play().catch(() => {});
        else media.pause();
      };

      const syncMute = () => {
        container.dataset.muted = String(media.muted);
        mute.setAttribute('aria-label', media.muted ? 'Unmute video' : 'Mute video');
      };

      const syncFullscreen = () => {
        const active = document.fullscreenElement === container || document.webkitFullscreenElement === container;
        container.dataset.fullscreen = String(active);
        fullscreen.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
      };

      toggle.addEventListener('click', togglePlayback);
      media.addEventListener('click', togglePlayback);
      media.addEventListener('play', syncPlayback);
      media.addEventListener('pause', syncPlayback);
      media.addEventListener('ended', syncPlayback);
      media.addEventListener('loadedmetadata', syncTime);
      media.addEventListener('durationchange', syncTime);
      media.addEventListener('timeupdate', syncTime);

      progress.addEventListener('input', () => {
        media.currentTime = Number(progress.value);
        syncTime();
      });

      mute.addEventListener('click', () => {
        media.muted = !media.muted;
        syncMute();
      });

      const canFullscreen = Boolean(container.requestFullscreen || container.webkitRequestFullscreen);
      if (!canFullscreen) fullscreen.hidden = true;
      fullscreen.addEventListener('click', () => {
        const active = document.fullscreenElement || document.webkitFullscreenElement;
        const action = active
          ? (document.exitFullscreen || document.webkitExitFullscreen)
          : (container.requestFullscreen || container.webkitRequestFullscreen);
        if (action) action.call(active ? document : container);
      });
      document.addEventListener('fullscreenchange', syncFullscreen);
      document.addEventListener('webkitfullscreenchange', syncFullscreen);

      container.addEventListener('keydown', (event) => {
        if (event.target.matches('button, input')) return;
        if (event.key === ' ' || event.key.toLowerCase() === 'k') {
          event.preventDefault();
          togglePlayback();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          media.currentTime = Math.max(0, Math.min(media.duration || 0, media.currentTime + (event.key === 'ArrowRight' ? 5 : -5)));
        } else if (event.key.toLowerCase() === 'm') {
          media.muted = !media.muted;
          syncMute();
        } else if (event.key.toLowerCase() === 'f' && !fullscreen.hidden) {
          fullscreen.click();
        }
      });

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) media.pause();
      });

      media.addEventListener('error', () => {
        container.classList.remove('is-enhanced');
        media.controls = true;
        toggle.hidden = true;
        controls.hidden = true;
      });

      syncPlayback();
      syncMute();
      syncTime();
      syncFullscreen();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
